import React from 'react';
import SummaryTypeSelector, { SummaryTypeSelectorProps } from './SummaryTypeSelector';
import ToneSelector, { ToneSelectorProps } from './ToneSelector';
import { cn } from '../../lib/utils';

export interface SummaryOptionsBarProps {
  summaryType: string;
  tone: string;
  onSummaryTypeChange: (value: string) => void;
  onToneChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
  containerClassName?: string;
  gap?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  direction?: 'row' | 'column';
  summaryTypeProps?: Partial<SummaryTypeSelectorProps>;
  toneProps?: Partial<ToneSelectorProps>;
  children?: React.ReactNode;
  showLabels?: boolean;
  // New props for conditional rendering
  showSummaryType?: boolean;
  showTone?: boolean;
}

const gapClasses = {
  xs: 'gap-1',
  sm: 'gap-2',
  md: 'gap-3',
  lg: 'gap-4',
  xl: 'gap-6',
};

const SummaryOptionsBar: React.FC<SummaryOptionsBarProps> = ({
  summaryType,
  tone,
  onSummaryTypeChange,
  onToneChange,
  disabled = false,
  className,
  containerClassName,
  gap = 'md',
  direction = 'row',
  summaryTypeProps = {},
  toneProps = {},
  children,
  showLabels = false,
  showSummaryType = true,
  showTone = true,
}) => {
  const directionClass = direction === 'row' ? 'flex-row' : 'flex-col';
  const gapClass = gapClasses[gap];

  // Don't render if both options are hidden
  if (!showSummaryType && !showTone) {
    return <>{children}</>;
  }

  return (
    <div className={cn('flex items-stretch', directionClass, gapClass, containerClassName)}>
      <div className={cn('flex flex-1 justify-between md:!justify-start', gapClass, className)}>
        {showSummaryType && (
          <SummaryTypeSelector
            value={summaryType}
            onChange={onSummaryTypeChange}
            disabled={disabled}
            showLabel={showLabels}
            {...summaryTypeProps}
          />
        )}
        {showTone && (
          <ToneSelector
            value={tone}
            onChange={onToneChange}
            disabled={disabled}
            showLabel={showLabels}
            {...toneProps}
          />
        )}
      </div>
      {children}
    </div>
  );
};

export default SummaryOptionsBar;